import { Signal } from "./Signal";

export {};

declare global {
    interface JsideaPlugins {
        adminBar: AdminBar;
    }

    interface ToolsConfig {}
}

export class AdminBar {
    private _element: HTMLElement;
    private _windows: HTMLElement;
    private _barWrapper: HTMLElement;
    private _bar: HTMLElement;
    private _ready: boolean = false;
    private _options: AdminBarItemOptions[] = [];
    private _usedKeys: Set<string> = new Set();

    constructor() {
        this._element = document.createElement("div");
        this._element.classList.add("jsidea-admin-bar");
        this._element.setAttribute("data-prevent-inert", "true");

        this._windows = document.createElement("div");
        this._windows.classList.add("jsidea-admin-bar__windows");
        this._element.appendChild(this._windows);

        this._barWrapper = document.createElement("div");
        this._barWrapper.classList.add("jsidea-admin-bar__bar-wrapper");
        this._element.appendChild(this._barWrapper);

        this._bar = document.createElement("div");
        this._bar.classList.add("jsidea-admin-bar__bar");
        this._barWrapper.appendChild(this._bar);

        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.classList.add("jsidea-admin-bar__toggle");
        toggle.setAttribute("aria-label", "Admin-Leiste umschalten");
        this._barWrapper.appendChild(toggle);
        this._element.classList.toggle(
            "jsidea-admin-bar--expanded",
            localStorage.getItem("jsidea-admin-bar-expanded") === "1",
        );
        const updateToggleIcon = () => {
            const expanded = this._element.classList.contains(
                "jsidea-admin-bar--expanded",
            );
            const icon = expanded ? "fa-chevron-left" : "fa-chevron-right";
            toggle.innerHTML = `<span class="fal ${icon}" aria-hidden="true"></span>`;
            toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
        };
        toggle.addEventListener("click", () => {
            this._element.classList.toggle("jsidea-admin-bar--expanded");
            updateToggleIcon();
            localStorage.setItem(
                "jsidea-admin-bar-expanded",
                this._element.classList.contains("jsidea-admin-bar--expanded")
                    ? "1"
                    : "0",
            );
        });
        updateToggleIcon();

        new DefaultItems(this);
    }

    public setReady(): void {
        if (this._ready) {
            return;
        }
        this._ready = true;
        for (const option of this._options) {
            this.createItem(option);
        }
        this._options = [];
        document.body.appendChild(this._element);
    }

    public add(options: AdminBarItemOptions): void {
        if (this._usedKeys.has(options.key)) {
            console.warn(
                `[jsidea.core] [AdminBar] Item with key "${options.key}" already exists in the admin bar. Skipping duplicate.`,
            );
            return;
        }
        this._usedKeys.add(options.key);

        if (this._ready) {
            this.createItem(options);
        } else {
            this._options.push(options);
        }
    }

    public has(key: string): boolean {
        return this._usedKeys.has(key);
    }

    private createItem(options: AdminBarItemOptions): void {
        const item = new AdminBarItem(options);
        this._bar.appendChild(item.element);
    }
}

/**
 * Processing state of an admin bar item.
 *
 * - `idle`     — default, nothing is going on
 * - `progress` — an async task is running (item is visually busy)
 * - `success`  — the last task finished successfully
 * - `warning`  — the last task finished with warnings
 * - `errored`  — the last task failed
 */
export type AdminBarItemState =
    | "idle"
    | "progress"
    | "success"
    | "warning"
    | "errored";

export const ADMIN_BAR_ITEM_STATES: readonly AdminBarItemState[] = [
    "idle",
    "progress",
    "success",
    "warning",
    "errored",
];

/**
 * A value that may differ per item state.
 * Resolution order: `disabled` → current state (`progress`, `success`,
 * `warning`, `errored`) → `active` → `default`.
 */
type StateValue<T> =
    | ({
          default?: T;
          active?: T;
          disabled?: T;
      } & Partial<Record<Exclude<AdminBarItemState, "idle">, T>>)
    | T;

interface AdminBarItemOptions {
    key: string;
    icon?: StateValue<string>;
    label?: StateValue<string>;
    tooltip?: StateValue<string>;
    html?: StateValue<string>;
    htmlElement?: StateValue<HTMLElement>;
    /** Initial processing state. Defaults to `idle`. */
    state?: AdminBarItemState;
    onInit?: (item: AdminBarItem) => void;
    onClick?: (item: AdminBarItem) => void;
    onToggle?: (item: AdminBarItem) => void;
    onStateChange?: (item: AdminBarItem) => void;
}

export class AdminBarItem {
    public readonly element: HTMLButtonElement;

    public onClick = Signal.create<[AdminBarItem]>();
    public onToggle = Signal.create<[AdminBarItem]>();
    public onStateChange = Signal.create<[AdminBarItem]>();

    private _key: string;
    private _disposables: DisposableObject[] = [];
    private _isDirty: boolean = false;
    private _icon: StateValue<string>;
    private _label: StateValue<string>;
    private _tooltip: StateValue<string>;
    private _active: boolean = false;
    private _disabled: boolean = false;
    private _state: AdminBarItemState = "idle";
    private _stateResetTimeout: ReturnType<typeof setTimeout> | null = null;
    private _html: StateValue<string>;
    private _htmlElement: StateValue<HTMLElement>;

    constructor(options: AdminBarItemOptions) {
        this._key = options.key;
        this._icon = options.icon;
        this._label = options.label;
        this._tooltip = options.tooltip;
        this._html = options.html;
        this._htmlElement = options.htmlElement;
        this._state = options.state ?? "idle";

        this.element = document.createElement("button");
        this.element.type = "button";
        this.element.classList.add("jsidea-admin-bar__item");
        this.element.dataset.key = this._key;

        if (options.onClick) {
            this.onClick.add(options.onClick);
        }

        if (options.onToggle) {
            this.onToggle.add(options.onToggle);
        }

        if (options.onStateChange) {
            this.onStateChange.add(options.onStateChange);
        }

        this.element.addEventListener("click", () => {
            this.onClick.dispatch(this);
        });

        options.onInit?.(this);

        this.render();
    }

    private setDirty(): void {
        if (this._isDirty) return;
        this._isDirty = true;
        requestAnimationFrame(() => {
            this._isDirty = false;
            this.render();
        });
    }

    private render(): void {
        for (const disposable of this._disposables) {
            disposable.dispose();
        }
        this._disposables = [];

        // this.element.innerHTML = "";
        while (this.element.firstChild) {
            this.element.removeChild(this.element.firstChild);
        }

        const tooltip = this.getValue(this._tooltip);
        if (tooltip) {
            this.element.setAttribute("title", tooltip);
        } else {
            this.element.removeAttribute("title");
        }

        this.element.classList.toggle(
            "jsidea-admin-bar__item--active",
            this._active,
        );
        this.element.classList.toggle(
            "jsidea-admin-bar__item--disabled",
            this._disabled,
        );
        for (const state of ADMIN_BAR_ITEM_STATES) {
            this.element.classList.toggle(
                `jsidea-admin-bar__item--${state}`,
                this._state === state,
            );
        }
        this.element.dataset.state = this._state;
        this.element.setAttribute(
            "aria-busy",
            this._state === "progress" ? "true" : "false",
        );

        const icon = this.getValue(this._icon);
        if (icon) {
            const iconElement = document.createElement("div");
            iconElement.className = "jsidea-admin-bar__item-icon " + icon;
            this.element.appendChild(iconElement);
        }

        const label = this.getValue(this._label);
        if (label) {
            const labelElement = document.createElement("div");
            labelElement.classList.add("jsidea-admin-bar__item-label");
            labelElement.textContent = label;
            this.element.appendChild(labelElement);
        }

        const html = this.getValue(this._html);
        if (html) {
            this.element.insertAdjacentHTML("beforeend", html);
        }

        const htmlElement = this.getValue(this._htmlElement);
        if (htmlElement) {
            this.element.appendChild(htmlElement);
        }
    }

    private getValue<T = any>(v: StateValue<T>): T | undefined {
        if (!jsidea.isLiteral(v) || v === null) {
            return v as T;
        }
        if (this._disabled && (v as any).disabled !== undefined) {
            return (v as any).disabled;
        }
        if (this._state !== "idle" && (v as any)[this._state] !== undefined) {
            return (v as any)[this._state];
        }
        if (this._active && (v as any).active !== undefined) {
            return (v as any).active;
        }
        return (v as any).default;
    }

    public get icon(): StateValue<string> {
        return this._icon;
    }

    public set icon(value: StateValue<string>) {
        if (this._icon === value) {
            return;
        }
        this._icon = value;
        this.setDirty();
    }

    public get label(): StateValue<string> {
        return this._label;
    }

    public set label(value: StateValue<string>) {
        if (this._label === value) {
            return;
        }
        this._label = value;
        this.setDirty();
    }

    public get tooltip(): StateValue<string> {
        return this._tooltip;
    }

    public set tooltip(value: StateValue<string>) {
        if (this._tooltip === value) {
            return;
        }
        this._tooltip = value;
        this.setDirty();
    }

    public get html(): StateValue<string> {
        return this._html;
    }

    public set html(value: StateValue<string>) {
        if (this._html === value) {
            return;
        }
        this._html = value;
        this.setDirty();
    }

    public get htmlElement(): StateValue<HTMLElement> {
        return this._htmlElement;
    }

    public set htmlElement(value: StateValue<HTMLElement>) {
        if (this._htmlElement === value) {
            return;
        }
        this._htmlElement = value;
        this.setDirty();
    }

    public get active(): boolean {
        return this._active;
    }

    public set active(value: boolean) {
        if (this._active === value) {
            return;
        }
        this._active = value;
        this.setDirty();
        this.onToggle.dispatch(this);
    }

    public get disabled(): boolean {
        return this._disabled;
    }

    public set disabled(value: boolean) {
        if (this._disabled === value) {
            return;
        }
        this._disabled = value;
        this.setDirty();
    }

    /**
     * Current processing state (`idle`, `progress`, `success`, `warning`, `errored`).
     * Setting it re-renders the item, applies the matching
     * `jsidea-admin-bar__item--<state>` class and dispatches `onStateChange`.
     */
    public get state(): AdminBarItemState {
        return this._state;
    }

    public set state(value: AdminBarItemState) {
        this.setState(value);
    }

    /**
     * Set the processing state.
     *
     * @param value      New state.
     * @param resetAfter Optional delay in ms after which the item falls
     *                   back to `idle` (useful for `success`/`errored`).
     *                   A pending reset is cancelled on every state change.
     */
    public setState(value: AdminBarItemState, resetAfter?: number): void {
        if (this._stateResetTimeout !== null) {
            clearTimeout(this._stateResetTimeout);
            this._stateResetTimeout = null;
        }

        if (resetAfter !== undefined && resetAfter >= 0 && value !== "idle") {
            this._stateResetTimeout = setTimeout(() => {
                this._stateResetTimeout = null;
                this.setState("idle");
            }, resetAfter);
        }

        if (this._state === value) {
            return;
        }
        this._state = value;
        this.setDirty();
        this.onStateChange.dispatch(this);
    }

    /** Shortcut for `state === "progress"`. */
    public get isBusy(): boolean {
        return this._state === "progress";
    }

    public store(key: string, value: any): void {
        try {
            localStorage.setItem(
                `jsidea-admin-bar-item-${this._key}-${key}`,
                JSON.stringify(value),
            );
        } catch (e) {
            console.warn("Unable to store item in localStorage", e);
        }
    }

    public retrieve<T = any>(key: string): T | null {
        try {
            const item = localStorage.getItem(
                `jsidea-admin-bar-item-${this._key}-${key}`,
            );
            if (item === null) {
                return null;
            }
            return JSON.parse(item) as T;
        } catch (e) {
            console.warn("Unable to retrieve item from localStorage", e);
            return null;
        }
    }
}

class DefaultItems {
    constructor(private _bar: AdminBar) {
        this.addOutlineDebug();
        this.addSiteLoadDebug();
        this.addFpsDebug();
    }

    /**
     * Add outline debug info to the admin bar
     */
    private addFpsDebug() {
        this._bar.add({
            key: "fps",
            label: "--",
            tooltip: "Frames Per Second",
            icon: "fal fa-tachometer-alt-fast",
            onInit: (item) => {
                let showCanvas = item.retrieve<boolean>("show-canvas") || false;

                const canvas = document.createElement("canvas");
                canvas.classList.add("debug-view-window__fps-canvas");
                canvas.width = 50;
                canvas.height = 22;

                if (showCanvas) {
                    item.htmlElement = canvas;
                }

                item.onClick.add(() => {
                    showCanvas = !showCanvas;
                    item.store("show-canvas", showCanvas);
                    if (showCanvas) {
                        item.htmlElement = canvas;
                    } else {
                        item.htmlElement = null;
                    }
                });

                const ctx = canvas.getContext("2d", {
                    willReadFrequently: true,
                });
                ctx.imageSmoothingEnabled = false;
                ctx.lineWidth = 1;

                let value = "";

                const render = () => {
                    const v = Math.round(fps).toString();
                    if (value !== v) {
                        value = v;
                        item.label = value;
                    }

                    if (!showCanvas) {
                        return;
                    }

                    //shift
                    ctx.globalCompositeOperation = "copy";
                    ctx.drawImage(ctx.canvas, -2, 0);
                    ctx.globalCompositeOperation = "source-over";

                    ctx.beginPath();
                    ctx.strokeStyle = "#0F0";
                    ctx.lineWidth = 2;
                    // ctx.moveTo(canvas.width - 0.5, canvas.height);#
                    const x = canvas.width - 1;
                    const y =
                        canvas.height -
                        canvas.height * jsidea.Calc.clamp(fps / 60, 0, 1);

                    ctx.moveTo(x, y);
                    ctx.lineTo(x, canvas.height);
                    ctx.stroke();

                    // ctx.moveTo(x, y);
                    // ctx.lineTo(x, y + 1);
                    // ctx.stroke();
                };
                let onDraw = jsidea.Signal.create();
                onDraw.throttle(render, 100);

                let fps = 0;
                let count = 0;
                const times: number[] = [];
                jsidea.Tick.add(() => {
                    const now = performance.now();
                    while (times.length > 0 && times[0] <= now - 1000) {
                        times.shift();
                    }
                    times.push(now);
                    fps = times.length;
                    if (count > 80) {
                        onDraw.dispatch();
                    } else {
                        count++;
                    }
                });
            },
        });
    }

    /**
     * Add outline debug info to the admin bar
     */
    private addSiteLoadDebug() {
        this._bar.add({
            key: "site-load",
            icon: "fal fa-stopwatch",
            onInit: (item) => {
                // let startTime = performance.timing.navigationStart;
                // let domContentLoadedTime =
                //     performance.timing.domContentLoadedEventEnd - startTime;
                // let loadTime = performance.timing.loadEventEnd - startTime;

                // item.tooltip = `DOM Content Loaded: ${domContentLoadedTime} ms\nTotal Load Time: ${loadTime} ms`;
                // item.html = `⚡ ${loadTime} ms`;

                const nav = window.performance.getEntriesByType("navigation");
                if (nav && nav.length > 0) {
                    const v: any = nav[0];
                    const val = (v.responseStart - v.requestStart) / 1000;
                    item.label = isNaN(val) ? "?" : val.toFixed(2);
                    item.tooltip = `Time to First Byte in seconds`;
                }
            },
        });
    }

    /**
     * Add outline debug info to the admin bar
     */
    private addOutlineDebug() {
        this._bar.add({
            key: "outline",
            icon: "fal fa-border-all",
            tooltip: "Show outline",
            onClick: (item) => {
                item.active = !item.active;
            },
            onInit: (item) => {
                item.active = item.retrieve<boolean>("active") || false;

                let isActive = false;
                const disposables: DisposableObject[] = [];
                let targetElement: HTMLElement | null = null;

                const infoBox = document.createElement("div");
                infoBox.classList.add("jsidea-g-debug-outline-info-box");

                const bounds = document.createElement("div");
                bounds.setAttribute("inert", "true");
                bounds.classList.add("jsidea-g-debug-outline-bounds");

                const setTargetElement = (el: HTMLElement | null) => {
                    if (targetElement === el) {
                        return;
                    }
                    if (targetElement) {
                        targetElement.classList.remove(
                            "jsidea-g-debug-outline-target",
                        );
                    }
                    targetElement = el;
                    if (targetElement) {
                        targetElement.classList.add(
                            "jsidea-g-debug-outline-target",
                        );
                    }
                };

                const updateTargetElement = () => {
                    if (!targetElement) {
                        if (bounds.parentElement) {
                            bounds.parentElement.removeChild(bounds);
                        }
                        return;
                    }

                    const rect = targetElement.getBoundingClientRect();
                    bounds.style.width = rect.width + "px";
                    bounds.style.height = rect.height + "px";
                    bounds.style.transform = `translate(${
                        rect.left
                    }px, ${rect.top}px)`;

                    if (!bounds.parentElement) {
                        document.body.appendChild(bounds);
                    }
                };

                const updateInfoBox = () => {
                    if (!targetElement) {
                        if (infoBox.parentElement) {
                            infoBox.parentElement.removeChild(infoBox);
                        }
                        return;
                    }

                    let html = ``;

                    if (window.devicePixelRatio !== 1) {
                        html += `<div>Width: ${targetElement.offsetWidth} | DPX ${Math.round(targetElement.offsetWidth * window.devicePixelRatio)}</div>`;
                        html += `<div>Height: ${targetElement.offsetHeight} | DPX ${Math.round(targetElement.offsetHeight * window.devicePixelRatio)}</div>`;
                        html += `<div>Device Pixel Ratio: ${window.devicePixelRatio.toFixed(2)}</div>`;
                    } else {
                        html += `<div>Width: ${targetElement.offsetWidth}</div>`;
                        html += `<div>Height: ${targetElement.offsetHeight}</div>`;
                    }

                    let style = window.getComputedStyle(targetElement);

                    // html += `<div>Classes: ${targetElement.className}</div>`;
                    // html += `<div>ID: ${targetElement.id || "(none)"}</div>`;
                    html += `<div>Font Size: ${style.fontSize}</div>`;
                    html += `<div>Margin: ${style.marginTop} ${style.marginRight} ${style.marginBottom} ${style.marginLeft}</div>`;
                    html += `<div>Padding: ${style.paddingTop} ${style.paddingRight} ${style.paddingBottom} ${style.paddingLeft}</div>`;
                    // html += `<div>Border: ${style.borderWidth} ${style.borderStyle} ${style.borderColor}</div>`;

                    html += `<div>Tag: &lt;${targetElement.tagName.toLowerCase()}&gt;</div>`;

                    infoBox.innerHTML = html;

                    const rect = targetElement.getBoundingClientRect();
                    infoBox.style.transform = `translate(${Math.max(
                        rect.left,
                        0,
                    )}px, ${Math.max(rect.top - infoBox.offsetHeight, 0)}px)`;

                    if (!infoBox.parentElement) {
                        document.body.appendChild(infoBox);
                    }
                };

                let clientX = 0;
                let clientY = 0;
                const updateListener = () => {
                    if (item.active === isActive) {
                        return;
                    }
                    isActive = item.active;
                    if (isActive) {
                        disposables.push(
                            // jsidea.event.on({
                            //     type: "mouseenter",
                            //     selector: ":hover:not(:has(:hover))",
                            //     capture: true,
                            //     listener: (e) => {
                            //         setTargetElement(e.target);
                            //     }
                            // }),
                            jsidea.event.on({
                                type: "mousemove",
                                capture: true,
                                listener: (e) => {
                                    clientX = e.event.clientX;
                                    clientY = e.event.clientY;
                                },
                            }),
                            jsidea.Tick.add(() => {
                                setTargetElement(
                                    document.querySelector(
                                        `:where(
        *:not(
            .jsidea-admin-bar,
            .jsidea-admin-bar *,
            #wpadminbar,
            #wpadminbar *,
            .jsidea-g-debug-outline-info-box,
            .jsidea-g-debug-outline-info-box *
        )
    ):hover:not(:has(:hover))`,
                                    ) as HTMLElement,
                                );
                                updateTargetElement();
                                updateInfoBox();

                                // setTargetElement(
                                //     document.elementFromPoint(
                                //         clientX,
                                //         clientY,
                                //     ) as HTMLElement,
                                // );
                            }),
                        );
                    } else {
                        setTargetElement(null);
                        updateTargetElement();
                        updateInfoBox();
                        for (const disposable of disposables) {
                            disposable.dispose();
                        }
                        disposables.length = 0;
                    }
                };
                updateListener();
                item.onToggle.add(updateListener);

                const updateBodyClass = () => {
                    document.body.classList.toggle(
                        "jsidea-g-debug-outline",
                        item.active,
                    );
                };

                item.onToggle.add(() => {
                    item.store("active", item.active);
                    updateBodyClass();
                });

                updateBodyClass();
            },
        });
    }
}
